import React, {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
} from "react";
import { CheckCircle2, Pause, Play, RotateCcw, X } from "lucide-react";
import { useReviewMode } from "../context/ReviewModeContext";
import { WORKFLOW_OVERVIEW_STEPS } from "./workflowOverviewSteps";
import TransitMapCanvas from "./TransitMapCanvas";
import {
  getTransitMapProgress,
  TRANSIT_MAP_INITIAL_STATE,
  TRANSIT_MAP_PHASES,
  transitMapReducer,
} from "./transitMapMachine";
import "./TransitMap.css";

const LAST_STATION_INDEX = WORKFLOW_OVERVIEW_STEPS.length - 1;
const TRANSIT_WORK_ITEM_ID = "REQ-069684";

function getStationStatus(state, progress) {
  if (state.phase === TRANSIT_MAP_PHASES.SETTLING) {
    return "Preparing route";
  }
  if (state.phase === TRANSIT_MAP_PHASES.IN_TRANSIT) {
    return "En route";
  }
  if (state.phase === TRANSIT_MAP_PHASES.DECISION) {
    return "Underwriting required?";
  }
  if (state.phase === TRANSIT_MAP_PHASES.BRANCH_TRAVEL) {
    return "YES / NO routing";
  }
  if (state.phase === TRANSIT_MAP_PHASES.UNDERWRITING_REVIEW) {
    return progress < 0.72 ? "UW Verifying" : "UW Verified";
  }
  if (state.phase === TRANSIT_MAP_PHASES.FINALIZE_YES) {
    return "Completing";
  }
  if (
    state.stationIndex === 4 &&
    state.phase === TRANSIT_MAP_PHASES.AT_STATION
  ) {
    if (progress < 0.3) {
      return "Assigned";
    }
    if (progress < 0.75) {
      return "Verifying";
    }
    return "Verified";
  }
  return WORKFLOW_OVERVIEW_STEPS[state.stationIndex].status;
}

export default function TransitMap({ onBack }) {
  const { setReviewMode } = useReviewMode();
  const [state, dispatch] = useReducer(
    transitMapReducer,
    TRANSIT_MAP_INITIAL_STATE,
  );
  const lastFrameRef = useRef(null);

  useLayoutEffect(() => {
    setReviewMode(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      setReviewMode(false);
    };
  }, [setReviewMode]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onBack();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBack]);

  useEffect(() => {
    if (!state.playing) {
      lastFrameRef.current = null;
      return undefined;
    }

    let frameId;
    const frame = (timestamp) => {
      if (lastFrameRef.current === null) {
        lastFrameRef.current = timestamp;
      }
      const deltaMs = Math.min(100, timestamp - lastFrameRef.current);
      lastFrameRef.current = timestamp;
      dispatch({
        type: "TICK",
        deltaMs,
        lastStationIndex: LAST_STATION_INDEX,
      });
      frameId = window.requestAnimationFrame(frame);
    };

    frameId = window.requestAnimationFrame(frame);
    return () => {
      window.cancelAnimationFrame(frameId);
      lastFrameRef.current = null;
    };
  }, [state.playing, state.journeyId]);

  const progress = getTransitMapProgress(state);
  const complete = state.phase === TRANSIT_MAP_PHASES.COMPLETE;
  const moving = state.phase === TRANSIT_MAP_PHASES.IN_TRANSIT;
  const settling = state.phase === TRANSIT_MAP_PHASES.SETTLING;
  const deciding = state.phase === TRANSIT_MAP_PHASES.DECISION;
  const branching = state.phase === TRANSIT_MAP_PHASES.BRANCH_TRAVEL;
  const underwritingReview =
    state.phase === TRANSIT_MAP_PHASES.UNDERWRITING_REVIEW;
  const finalizingYes = state.phase === TRANSIT_MAP_PHASES.FINALIZE_YES;
  const itemStarted = complete || state.stationIndex >= 1;
  const displayIndex = moving
    ? Math.min(LAST_STATION_INDEX, state.stationIndex + 1)
    : state.stationIndex;
  const displayStep = WORKFLOW_OVERVIEW_STEPS[displayIndex];
  const stationStatus = getStationStatus(state, progress);
  const conditionalContext = deciding || branching
    ? {
        title: "Underwriting Required?",
        color: WORKFLOW_OVERVIEW_STEPS[4].color,
        status: branching ? "YES and NO paths active" : "Conditional decision",
        description:
          "Operations verification is complete. The work item now follows both conditional outcomes simultaneously.",
        actions: [
          "YES — Route to Underwriting for an additional verification",
          "NO — Complete and make available for the next operation",
        ],
      }
    : finalizingYes
      ? {
          title: "Completing YES Path",
          color: WORKFLOW_OVERVIEW_STEPS[5].color,
          status: "Underwriting verified",
          description:
            "Underwriting verification is complete and the YES path is joining the final completion point.",
          actions: [
            "Record the Underwriting verification outcome",
            "Make the completed item available for the next operation",
          ],
        }
      : complete
        ? {
            title: "Available for Next Operation",
            color: "#2563eb",
            status: "Completed",
            description:
              "Both conditional outcomes finish at the same completion point.",
            actions: [
              "NO path completed after Operations verification",
              "YES path completed after Underwriting verification",
            ],
          }
        : null;
  const contextStep = conditionalContext || displayStep;
  const displayStatus = conditionalContext
    ? conditionalContext.status
    : moving
      ? "En route"
      : stationStatus;

  return (
    <main
      className={`transit-page${state.playing ? "" : " is-paused"}`}
      data-testid="transit-map-page"
    >
      <button type="button" className="transit-close" onClick={onBack}>
        <X size={14} />
        Close
      </button>

      <header className="transit-header">
        <div className="transit-titleBlock">
          <h1>Workflow Overview</h1>
          <p>
            A work item travels through six connected enterprise workflow stations.
          </p>
        </div>

        <div className="transit-live">
          <div className="transit-liveLine" />
          <div>
            <kbd>Live journey</kbd>
            <b>{itemStarted ? TRANSIT_WORK_ITEM_ID : "Incoming email"}</b>
            <span>
              {complete
                ? "Both outcomes complete"
                : settling
                  ? "Route preparing"
                  : moving
                    ? `En route to Station ${String(displayIndex + 1).padStart(2, "0")}`
                    : deciding
                      ? "Conditional decision"
                      : branching
                        ? "YES and NO paths active"
                        : underwritingReview
                          ? "YES at Underwriting · NO complete"
                          : finalizingYes
                            ? "YES path completing"
                            : `Station ${String(displayIndex + 1).padStart(2, "0")} of 06`}
            </span>
            <strong>{contextStep.title}</strong>
          </div>
        </div>

        <div className="transit-controls" aria-label="Transit Map controls">
          {complete ? (
            <button type="button" onClick={() => dispatch({ type: "REPLAY" })}>
              <RotateCcw size={13} />
              Replay
            </button>
          ) : state.playing ? (
            <button type="button" onClick={() => dispatch({ type: "PAUSE" })}>
              <Pause size={13} />
              Pause
            </button>
          ) : (
            <button type="button" onClick={() => dispatch({ type: "RESUME" })}>
              <Play size={13} />
              Resume
            </button>
          )}
        </div>
      </header>

      <TransitMapCanvas
        phase={state.phase}
        stationIndex={state.stationIndex}
        phaseProgress={progress}
        stationStatus={stationStatus}
        workItemId={TRANSIT_WORK_ITEM_ID}
      />

      <aside className="transit-context" aria-live="polite">
        <div
          key={`${state.phase}-${displayIndex}`}
          className={`transit-contextInner${complete ? " is-complete" : ""}`}
          style={{ "--transit-accent": contextStep.color }}
        >
          <div className="transit-contextKicker">
            {complete ? (
              <>
                <CheckCircle2 size={13} />
                Journey complete
              </>
            ) : deciding || branching ? (
              "Conditional route"
            ) : finalizingYes ? (
              "Completing workflow"
            ) : moving ? (
              "Work item in transit"
            ) : settling ? (
              "Route initializing"
            ) : (
              "Current station"
            )}
          </div>

          <div className="transit-contextItem">{TRANSIT_WORK_ITEM_ID}</div>
          <span className="transit-contextLabel">
            {deciding || branching
              ? "Decision"
              : complete || finalizingYes
                ? "Outcome"
                : moving
                  ? "Destination station"
                  : "Current station"}
          </span>
          <h2>{contextStep.title}</h2>
          <div className="transit-contextStatus">
            <i />
            {displayStatus}
          </div>
          <p>{contextStep.description}</p>

          <div className="transit-contextDivider" />
          <span className="transit-contextLabel">At this station</span>
          <ul>
            {contextStep.actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>

          {complete ? (
            <button
              type="button"
              className="transit-replay"
              onClick={() => dispatch({ type: "REPLAY" })}
            >
              <RotateCcw size={13} />
              Replay route
            </button>
          ) : null}
        </div>
      </aside>
    </main>
  );
}
